import AuthUtil from '../../redux/AuthUtil';
import { store } from '../../redux/store';
import { RAFFLE } from '../global/Server/endpoints';

// The raffle module talks to the server directly (like TicketOut and
// MasterRecordForm): its state is one screen's working state, not something
// other screens read from the store.
//
// Every call resolves to the server's payload. A transport failure resolves
// to { success: false, offline: true } so a dropped connection mid-draw is
// something the screen can say out loud and retry (R66), not an exception.

const OFFLINE_MESSAGE =
  'Connection lost. Nothing was lost on the server — try again when you are back online.';

// WHO THE RAFFLE BELONGS TO.
//
// Two employees share one station: Liz logs in, Hasnain picks his profile in
// the navbar switcher. That switch is client-side only — the bearer token
// never changes — so without this every run, draw, payment and History list
// arrives at the server as Liz's, and one account collects the whole floor's
// raffles.
//
// auth.userInfo carries the SELECTED profile (the header's employee switcher
// merges it in on every switch, see reducers/auth UPDATE_USER_ID), which is
// exactly what TicketOut and Bonus send as `userId`. Read here rather than
// threaded through props so no call site can be missed — a forgotten one
// would silently file the entry under the wrong employee again.
//
// The server never takes the id on trust: RaffleModuleService.actor accepts
// it only for a co-worker at the same company, and manager rights stay with
// the signed-in account either way.
const actingUserId = () => {
  try {
    return store.getState()?.auth?.userInfo?.userId || undefined;
  } catch (e) {
    return undefined;
  }
};

const unwrap = (promise) =>
  promise
    .then((res) => res?.data || { success: false, message: 'No response' })
    .catch((err) => {
      if (err?.response?.data?.message) return err.response.data;
      return { success: false, offline: true, message: OFFLINE_MESSAGE };
    });

const post = (url, data) =>
  unwrap(AuthUtil.postRequest(url, { ...data, userId: actingUserId() }));

export const raffleApi = {
  bootstrap: (locationId) => post(RAFFLE.BOOTSTRAP, { locationId }),
  currentRun: (locationId, runId) =>
    post(RAFFLE.CURRENT_RUN, { locationId, runId }),
  // Always the caller's own clock session — the server takes no date range.
  history: (locationId) => post(RAFFLE.HISTORY, { locationId }),
  winners: (locationId, filter) => post(RAFFLE.WINNERS, { locationId, filter }),
  customerWins: (locationId, customerId) =>
    post(RAFFLE.CUSTOMER_WINS, { locationId, customerId }),
  // The auto raffle numbers a customer holds this cycle — what gets printed.
  customerNumbers: (locationId, customerId, drawingId) =>
    post(RAFFLE.CUSTOMER_NUMBERS, { locationId, customerId, drawingId }),
  searchCustomers: (locationId, search) =>
    post(RAFFLE.SEARCH_CUSTOMERS, { locationId, search }),
  addCustomer: (data) => post(RAFFLE.ADD_CUSTOMER, data),

  saveSettings: (data) => post(RAFFLE.SAVE_SETTINGS, data),
  saveDrawing: (data) => post(RAFFLE.SAVE_DRAWING, data),
  deleteDrawing: (data) => post(RAFFLE.DELETE_DRAWING, data),
  saveBook: (data) => post(RAFFLE.SAVE_BOOK, data),
  deleteBook: (data) => post(RAFFLE.DELETE_BOOK, data),

  updateGivenOut: (data) => post(RAFFLE.UPDATE_GIVEN_OUT, data),
  confirmTickets: (runId) => post(RAFFLE.CONFIRM_TICKETS, { runId }),
  startRun: (data) => post(RAFFLE.START_RUN, data),
  draw: (runId) => post(RAFFLE.DRAW, { runId }),
  discardDraw: (drawId) => post(RAFFLE.DISCARD_DRAW, { drawId }),
  closeRun: (runId) => post(RAFFLE.CLOSE_RUN, { runId }),
  carryPot: (runId) => post(RAFFLE.CARRY_POT, { runId }),
  startNewSeries: (data) => post(RAFFLE.START_NEW_SERIES, data),

  // R65 — the drawId is the single-use token; a retry of the same drawId
  // returns the original raffle instead of paying twice.
  pay: ({ drawId, amount, customerId, photo }) => {
    const form = new FormData();
    form.append('drawId', drawId);
    form.append('amount', String(amount));
    form.append('customerId', customerId);
    // Multipart, so it can't go through post() — append it by hand, or the
    // payment is the one write still filed under whoever logged in.
    form.append('userId', actingUserId() || '');
    form.append('photo', photo, 'raffle-photo.jpg');
    return unwrap(AuthUtil.postMultipartRequest(RAFFLE.PAY, form));
  },

  editRaffle: (data) => post(RAFFLE.EDIT_RAFFLE, data),
  voidRaffle: (raffleId, reason) =>
    post(RAFFLE.VOID_RAFFLE, { raffleId, reason }),
};
